import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'
import type {TFunction} from 'i18next'
import {Skeleton} from '@/components/ui/skeleton'
import {ListEmptyState} from '@/components/section-card'
import {Hint} from '@/components/ui/hint'
import {ResultadosKpis} from '@/components/bateria/resultados-kpis'
import {ResultadosSegmentador, type GroupVista} from '@/components/bateria/resultados-segmentador'
import {ResultadosCaracterizacion} from '@/components/bateria/resultados-caracterizacion'
import {useAgregados, type Agregados} from '@/hooks/bateria/use-bateria'

function fichaLabelFor(t: TFunction, campo: string) {
  return (key: string) => {
    if (key === '(sin dato)') return t('admin.bateria.resultados.sinDato')
    return t(`admin.bateria.ficha.opcion.${campo}.${key}`, {defaultValue: key})
  }
}

/** A cut compares only with ≥2 segments carrying data: a lone segment just repeats the consolidado. */
function segmentableGroups(agregados: Agregados, t: TFunction): GroupVista[] {
  const groups: GroupVista[] = [
    {key: 'area', data: agregados.por_area, nPor: agregados.n_validados_por_area},
    {key: 'cargo', data: agregados.por_cargo, nPor: agregados.n_validados_por_cargo},
    {key: 'sede', data: agregados.por_sede, nPor: agregados.n_validados_por_sede},
    {
      key: 'vinculacion',
      data: agregados.por_tipo_vinculacion,
      nPor: agregados.n_validados_por_tipo_vinculacion,
    },
    {key: 'sexo', data: agregados.por_sexo, nPor: agregados.n_validados_por_sexo, labelFor: fichaLabelFor(t, 'sexo')},
    {
      key: 'nivel_estudios',
      data: agregados.por_nivel_estudios,
      nPor: agregados.n_validados_por_nivel_estudios,
      labelFor: fichaLabelFor(t, 'nivel_estudios'),
    },
    {
      key: 'estrato',
      data: agregados.por_estrato,
      nPor: agregados.n_validados_por_estrato,
      labelFor: fichaLabelFor(t, 'estrato'),
    },
    {
      key: 'tipo_vivienda',
      data: agregados.por_tipo_vivienda,
      nPor: agregados.n_validados_por_tipo_vivienda,
      labelFor: fichaLabelFor(t, 'tipo_vivienda'),
    },
    {
      key: 'tipo_cargo',
      data: agregados.por_tipo_cargo,
      nPor: agregados.n_validados_por_tipo_cargo,
      labelFor: fichaLabelFor(t, 'tipo_cargo'),
    },
    {
      key: 'tipo_contrato',
      data: agregados.por_tipo_contrato,
      nPor: agregados.n_validados_por_tipo_contrato,
      labelFor: fichaLabelFor(t, 'tipo_contrato'),
    },
    {
      key: 'tipo_salario',
      data: agregados.por_tipo_salario,
      nPor: agregados.n_validados_por_tipo_salario,
      labelFor: fichaLabelFor(t, 'tipo_salario'),
    },
  ]
  return groups.filter((g) => Object.keys(g.data).length >= 2)
}

export function ResultadosSection({
  businessId,
  evaluacionId,
  enabled,
  totalPaquetes,
}: {
  businessId: string
  evaluacionId: string
  enabled: boolean
  totalPaquetes: number
}) {
  const {t} = useTranslation()
  const {data: agregados} = useAgregados(businessId, evaluacionId, enabled)
  // Antes de las salidas tempranas (regla de hooks) y memoizado: `labelFor` es un cierre nuevo en cada
  // llamada y la gráfica de segmentos se re-aplicaría entera en vez de animar.
  const groupVistas = useMemo(() => (agregados ? segmentableGroups(agregados, t) : []), [agregados, t])

  if (!enabled) return null
  if (!agregados) return <Skeleton className="h-56 w-full" />

  const totalValidados = agregados.n_validados.total ?? 0
  if (totalValidados === 0) {
    return (
      <section data-testid="resultados-empty" className="space-y-3">
        <SectionHeader />
        <div className="surface">
          <ListEmptyState title={t('admin.bateria.resultados.emptyTitle')} />
        </div>
      </section>
    )
  }

  // Fewer than 3 validated makes any reading fragile, and forms with <3 don't publish in the informe.
  const lowN = totalValidados < 3

  return (
    <section data-testid="resultados-section" className="space-y-5">
      <SectionHeader />

      <ResultadosKpis consolidado={agregados.consolidado} validados={totalValidados} totalPaquetes={totalPaquetes} />

      {lowN && (
        <p className="notice-amber rounded-lg px-3.5 py-2.5 text-xs leading-snug" data-testid="resultados-low-n">
          {t('admin.bateria.resultados.calidadBajaN', {count: totalValidados})}
        </p>
      )}

      <ResultadosSegmentador agregados={agregados} groupVistas={groupVistas} />

      <ResultadosCaracterizacion
        porArea={agregados.por_area}
        porCargo={agregados.por_cargo}
        porSede={agregados.por_sede}
        porTipoVinculacion={agregados.por_tipo_vinculacion}
        nPorArea={agregados.n_validados_por_area}
        nPorCargo={agregados.n_validados_por_cargo}
        nPorSede={agregados.n_validados_por_sede}
        nPorTipoVinculacion={agregados.n_validados_por_tipo_vinculacion}
      />
    </section>
  )
}

function SectionHeader() {
  const {t} = useTranslation()
  return (
    <div>
      <h2 className="font-heading text-base text-foreground">{t('admin.bateria.resultados.title')}</h2>
      {/* T8: la custodia de datos de salud no se pliega: se queda impresa. */}
      <Hint className="mt-1">{t('admin.bateria.resultados.custodia')}</Hint>
    </div>
  )
}
